import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useIpcQuery } from '../hooks/useIpc';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { persistScaffoldFirst, persistCreatePrompt } from '../components/scaffold-first-storage';
import { RollbackWarningCard, invalidateGameLists } from './Dashboard';
import type { SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { unflattenIpcError } from '@volley/crucible-ipc-types/error-types';
import { buildPrereqError } from './prereq-error';
import { buildCreateHandoffRouteState } from './create-handoff';
import { Settings as SettingsIcon, Home, Mic, ChevronDown, Check, X } from 'lucide-react';
import type { CreateGameKickoffMode } from '../components/CreateGameInterviewDepthToggle';
import { deriveCreateDescription } from './create-description';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { useAppFrame } from '../AppFrameContext';
import { useClientCapabilities } from '../transport/context';
import { canBuildWithIngots } from '@volley/crucible-client';
import { deriveCreateNameFromConcept } from './create-name';

const DEPTH_OPTIONS: { value: CreateGameKickoffMode; label: string; description: string }[] = [
  { value: 'quick', label: 'Fast', description: 'A shorter questionnaire — quick to complete' },
  {
    // #1773 — zero-questions kickoff. Renderer-only mode: the create
    // payload maps it to `interviewDepth: 'quick'` + `interviewBestEffort:
    // true`, and the build proceeds with sensible defaults recorded.
    value: 'just-build',
    label: 'Just build',
    description: 'No questions — the agent makes sensible choices and builds immediately',
  },
  {
    value: 'advanced',
    label: 'Advanced',
    description: 'Author a full design doc in the Design tab — replaces the interview',
  },
];

type Stage = 'form' | 'creating' | 'success' | 'error';

export const NEW_GAME_DRAFT_STORAGE_KEY = 'crucible:new-game-draft:v1:app';

const DRAFT_PERSIST_DEBOUNCE_MS = 150;
const PROMPT_TEXTAREA_MIN_HEIGHT = 26;
const PROMPT_TEXTAREA_MAX_HEIGHT = 160;

interface NewGameDraft {
  concept: string;
  interviewDepth: CreateGameKickoffMode;
  buildWithIngots: boolean;
}

function isInterviewDepth(value: unknown): value is CreateGameKickoffMode {
  // #1773 — 'just-build' is a renderer-only kickoff mode that may be
  // persisted in the draft; it round-trips like the depths do.
  return value === 'quick' || value === 'full' || value === 'advanced' || value === 'just-build';
}

function normaliseNewGameDraftDepth(
  value: CreateGameKickoffMode,
  flags?: { designTab?: boolean },
): CreateGameKickoffMode {
  if (value === 'full') return 'quick';
  if (value === 'advanced' && flags?.designTab === false) return 'quick';
  return value;
}

function readNewGameDraft(): NewGameDraft {
  const fallback: NewGameDraft = { concept: '', interviewDepth: 'quick', buildWithIngots: false };
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(NEW_GAME_DRAFT_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      concept: typeof parsed.concept === 'string' ? parsed.concept : '',
      interviewDepth: isInterviewDepth(parsed.interviewDepth)
        ? normaliseNewGameDraftDepth(parsed.interviewDepth)
        : 'quick',
      buildWithIngots: parsed.buildWithIngots === true,
    };
  } catch {
    return fallback;
  }
}

function writeNewGameDraft(draft: NewGameDraft): void {
  if (typeof window === 'undefined') return;
  try {
    if (draft.concept.trim().length === 0) {
      window.localStorage.removeItem(NEW_GAME_DRAFT_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(
      NEW_GAME_DRAFT_STORAGE_KEY,
      JSON.stringify({
        concept: draft.concept,
        interviewDepth: draft.interviewDepth,
        buildWithIngots: draft.buildWithIngots,
        updatedAt: Date.now(),
      }),
    );
  } catch {
    // localStorage can be unavailable or full; keep typing responsive.
  }
}

function clearNewGameDraft(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(NEW_GAME_DRAFT_STORAGE_KEY);
  } catch {
    // localStorage can be unavailable; clearing in-memory state is enough.
  }
}

/**
 * Route state carried by the preview genre chip's "change" action (live-preview
 * scaffold-first): an explicit recast marker plus, when the per-game cache
 * still has it, the game's original create prompt. The marker distinguishes
 * "recast with no recoverable prompt" (→ blank start-over; the stored draft
 * does NOT seed the form) from an ordinary `/new-game` visit (→ draft-seeded
 * form, unchanged).
 */
export type RecastRouteState = { recast: true; prompt?: string };

/**
 * Build the `/new-game` route state for the chip's "change" action. Pure and
 * exported for unit tests (Workspace wires it straight into `navigate`); pairs
 * with {@link readRecastPromptFromState}. A missing / blank cached prompt still
 * recasts — there is just nothing to pre-fill, so the form starts blank rather
 * than restoring an unrelated stored draft.
 */
export function buildRecastRouteState(prompt: string | null): RecastRouteState {
  return typeof prompt === 'string' && prompt.trim().length > 0
    ? { recast: true, prompt }
    : { recast: true };
}

/**
 * Validate an incoming `/new-game` route state for a "recast" pre-fill. The
 * preview genre chip's "change" action (live-preview scaffold-first) navigates
 * here carrying the original create prompt so the user re-describes rather than
 * starting from a blank box. Route state is `unknown` — a crafted nav or a
 * stale history entry could hand us anything — so require the explicit `recast`
 * marker, then extract the trimmed prompt: a non-empty string pre-fills the
 * form; an absent / invalid / blank prompt yields `''` (recast with nothing
 * recoverable → blank start-over). Anything without the marker returns `null`
 * (→ plain draft-seeded New Game). Exported for unit tests; a pure boundary
 * guard (mirrors the chip's `isScaffoldFirstInference`).
 */
export function readRecastPromptFromState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const candidate = state as { recast?: unknown; prompt?: unknown };
  if (candidate.recast !== true) return null;
  return typeof candidate.prompt === 'string' ? candidate.prompt.trim() : '';
}

export function NewGame({
  initialStage,
  initialProgressMessages,
}: {
  /** Storybook/test only — seed a specific stage. */
  initialStage?: Stage;
  /** Storybook/test only — seed progress messages. */
  initialProgressMessages?: string[];
} = {}): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const flags = useFeatureFlags();
  const frame = useAppFrame();
  const capabilities = useClientCapabilities();
  const ingotScaffolderAvailable = canBuildWithIngots(capabilities);
  const designDepthAvailable = flags.designTab && capabilities.hasLocalFilesystem;
  const { data: ghUser } = useIpcQuery('github:get-user', undefined, { staleTime: 5 * 60_000 });
  const [draftSeed] = useState(readNewGameDraft);
  const location = useLocation();
  // Live-preview scaffold-first: the genre chip's "change" action navigates
  // here with a recast marker (+ the original prompt when the per-game cache
  // still has it) in route state. When present it seeds the form, taking
  // precedence over a stored draft — a recast with no recoverable prompt
  // deliberately starts from a BLANK box rather than restoring an unrelated
  // draft. Absent → the plain draft-seeded form, unchanged. Only the
  // first-mount value matters (it seeds `useState`).
  const recastPrompt = readRecastPromptFromState(location.state);
  const initialConcept = recastPrompt ?? draftSeed.concept;
  const initialBuildWithIngots = ingotScaffolderAvailable;

  // One-shot: once the form is seeded the recast state has done its job. Strip
  // it from the history entry (same-path replace) so a later Back/Forward
  // remount re-seeds from the stored draft (the user's latest edits) instead of
  // resurrecting — and re-persisting — the original prompt over them. Declared
  // BEFORE the newUi bounce below so that when both fire in the same flush the
  // bounce's navigate runs last and wins. Re-runs are no-ops: after the replace
  // the state is gone, so `recastPrompt` is null.
  useEffect(() => {
    if (recastPrompt !== null) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [recastPrompt, navigate, location.pathname]);

  // Hash URLs and bookmarks can land here even when the newUi flag is off.
  // Bounce back to Dashboard once the flag snapshot has loaded.
  useEffect(() => {
    if (flags.loaded && !flags.newUi) {
      navigate('/dashboard', { replace: true });
    }
  }, [flags.loaded, flags.newUi, navigate]);

  const [concept, setConcept] = useState(initialConcept);
  const [interviewDepth, setInterviewDepth] = useState<CreateGameKickoffMode>(
    draftSeed.interviewDepth,
  );
  const [buildWithIngots, setBuildWithIngots] = useState(initialBuildWithIngots);
  const [depthOpen, setDepthOpen] = useState(false);
  const [stage, setStage] = useState<Stage>(initialStage ?? 'form');
  const [progressMessages, setProgressMessages] = useState<string[]>(initialProgressMessages ?? []);
  const [errorState, setErrorState] = useState<SerialisedError | null>(null);
  const [rollbackWarning, setRollbackWarning] = useState<{
    message: string;
    recovery?: string;
  } | null>(null);

  const depthRef = useRef<HTMLDivElement>(null);
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const buildWithIngotsTouchedRef = useRef(false);
  const draftPersistTimerRef = useRef<number | null>(null);
  const latestDraftRef = useRef<NewGameDraft>({
    concept: initialConcept,
    interviewDepth: draftSeed.interviewDepth,
    buildWithIngots: initialBuildWithIngots,
  });

  useEffect(() => {
    if (ingotScaffolderAvailable) {
      if (buildWithIngotsTouchedRef.current || buildWithIngots) return;
      latestDraftRef.current = {
        ...latestDraftRef.current,
        buildWithIngots: true,
      };
      setBuildWithIngots(true);
      return;
    }
    if (!buildWithIngots) return;
    latestDraftRef.current = {
      ...latestDraftRef.current,
      buildWithIngots: false,
    };
    setBuildWithIngots(false);
  }, [buildWithIngots, ingotScaffolderAvailable]);

  useEffect(() => {
    if (!flags.loaded) return;
    const modeFlags = { designTab: designDepthAvailable };
    setInterviewDepth((current) => normaliseNewGameDraftDepth(current, modeFlags));
    latestDraftRef.current = {
      ...latestDraftRef.current,
      interviewDepth: normaliseNewGameDraftDepth(latestDraftRef.current.interviewDepth, modeFlags),
    };
  }, [flags.loaded, designDepthAvailable]);

  const cancelDraftPersistTimer = useCallback(() => {
    if (draftPersistTimerRef.current === null || typeof window === 'undefined') return;
    window.clearTimeout(draftPersistTimerRef.current);
    draftPersistTimerRef.current = null;
  }, []);

  const clearPromptDraft = useCallback(() => {
    cancelDraftPersistTimer();
    latestDraftRef.current = { concept: '', interviewDepth, buildWithIngots };
    clearNewGameDraft();
    setConcept('');
  }, [buildWithIngots, cancelDraftPersistTimer, interviewDepth]);

  const handleConceptChange = useCallback((value: string) => {
    latestDraftRef.current = {
      concept: value,
      interviewDepth: latestDraftRef.current.interviewDepth,
      buildWithIngots: latestDraftRef.current.buildWithIngots,
    };
    setConcept(value);
  }, []);

  const handleDepthSelect = useCallback((value: CreateGameKickoffMode) => {
    latestDraftRef.current = {
      concept: latestDraftRef.current.concept,
      interviewDepth: value,
      buildWithIngots: latestDraftRef.current.buildWithIngots,
    };
    setInterviewDepth(value);
    setDepthOpen(false);
  }, []);

  const handleBuildWithIngotsToggle = useCallback(() => {
    if (!ingotScaffolderAvailable) return;
    buildWithIngotsTouchedRef.current = true;
    setBuildWithIngots((current) => {
      const next = !current;
      latestDraftRef.current = {
        ...latestDraftRef.current,
        buildWithIngots: next,
      };
      return next;
    });
  }, [ingotScaffolderAvailable]);

  useEffect(() => {
    latestDraftRef.current = { concept, interviewDepth, buildWithIngots };
    cancelDraftPersistTimer();
    if (typeof window === 'undefined') return;
    draftPersistTimerRef.current = window.setTimeout(() => {
      writeNewGameDraft(latestDraftRef.current);
      draftPersistTimerRef.current = null;
    }, DRAFT_PERSIST_DEBOUNCE_MS);
  }, [buildWithIngots, cancelDraftPersistTimer, concept, interviewDepth]);

  useEffect(() => {
    return () => {
      cancelDraftPersistTimer();
      writeNewGameDraft(latestDraftRef.current);
    };
  }, [cancelDraftPersistTimer]);

  useLayoutEffect(() => {
    const prompt = promptRef.current;
    if (!prompt) return;
    prompt.style.height = 'auto';
    const nextHeight = Math.min(prompt.scrollHeight, PROMPT_TEXTAREA_MAX_HEIGHT);
    prompt.style.height = `${Math.max(nextHeight, PROMPT_TEXTAREA_MIN_HEIGHT)}px`;
    prompt.style.overflowY = prompt.scrollHeight > PROMPT_TEXTAREA_MAX_HEIGHT ? 'auto' : 'hidden';
  }, [concept]);

  // Close depth dropdown on outside click
  useEffect(() => {
    if (!depthOpen) return;
    const handler = (e: MouseEvent): void => {
      if (depthRef.current && !depthRef.current.contains(e.target as Node)) {
        setDepthOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [depthOpen]);

  useEffect(() => {
    if (stage !== 'creating') return;
    const unsub = window.crucible.on('game:create:progress', (event) => {
      if (event.message) setProgressMessages((prev) => [...prev, event.message]);
      if (event.warning)
        setRollbackWarning({
          message: event.warning,
          ...(event.recovery ? { recovery: event.recovery } : {}),
        });
      if (event.error) {
        setErrorState(unflattenIpcError(event.error));
        setStage('error');
      }
    });
    return unsub;
  }, [stage]);

  const handleCreate = useCallback(async () => {
    const conceptText = concept.trim();
    if (!conceptText) return;

    setStage('creating');
    setProgressMessages([]);
    setErrorState(null);
    setRollbackWarning(null);

    try {
      const verify = await window.crucible.invoke('tokens:verify');
      const prereqErr = buildPrereqError(verify);
      if (prereqErr) {
        setErrorState(prereqErr);
        setStage('error');
        return;
      }

      const ghMissing = verify.github.missingScopes ?? [];
      if (verify.github.status === 'invalid' && ghMissing.length > 0) {
        setErrorState({
          name: 'CrucibleError',
          message: verify.github.error ?? 'Your GitHub token is missing required scopes.',
          category: 'auth',
          retryable: false,
          code: 'CRUCIBLE-207',
          recovery:
            'Open Settings → Connections → GitHub, paste a token with the missing scopes ticked, then try again.',
        });
        setStage('error');
        return;
      }

      let displayNameToUse: string | undefined;
      try {
        const suggested = await window.crucible.invoke('game:suggest-name', { conceptText });
        displayNameToUse = suggested.displayName;
      } catch {
        /* non-fatal */
      }

      // #1824 — derive the on-disk / repo slug FROM the concept the user
      // typed, so the project folder + GitHub repo reflect what the game is
      // (e.g. "a peggle-style ball bouncer" → "peggle-style-ball-bouncer")
      // instead of a random codename. A numeric `-2`/`-3` suffix is added
      // ONLY when the clean name collides with an existing local game id;
      // common case is a clean, suffix-free name. Best-effort: fetch the
      // local game list for the collision check, and fall back to an empty
      // set (letting the core CRUCIBLE-201 pre-flight + recovery backstop a
      // collision) if the list isn't available on this transport.
      let existingGameIds: string[] = [];
      try {
        const games = await window.crucible.invoke('game:list:lite');
        existingGameIds = games.map((g) => g.gameId);
      } catch {
        /* non-fatal — collision check is best-effort; core backstops races */
      }
      const nameToUse = deriveCreateNameFromConcept(conceptText, existingGameIds);

      // #1773 — 'just-build' is a renderer-only kickoff mode: the wire
      // carries the persisted depth ('quick') plus the best-effort flag
      // the create core stamps onto the interview seed.
      const safeInterviewDepth = normaliseNewGameDraftDepth(interviewDepth, {
        designTab: designDepthAvailable,
      });
      const justBuild = safeInterviewDepth === 'just-build';
      const wireInterviewDepth = safeInterviewDepth === 'just-build' ? 'quick' : safeInterviewDepth;
      const autoKickoff = wireInterviewDepth !== 'advanced';
      const result = await window.crucible.invoke('game:create', {
        name: nameToUse,
        ...(displayNameToUse ? { displayName: displayNameToUse } : {}),
        description: deriveCreateDescription(conceptText),
        initialPrompt: conceptText,
        interviewDepth: wireInterviewDepth,
        ...(justBuild ? { interviewBestEffort: true } : {}),
        ...(buildWithIngots ? { buildWithIngots: true } : {}),
      });

      clearPromptDraft();
      invalidateGameLists(queryClient);
      setStage('success');
      // Live-preview scaffold-first (A2.3b): carry the inferred-genre outcome
      // into the Workspace via route state so the preview can paint the genre
      // chip for this create→preview session. Present ONLY when scaffold-first
      // ran (A2.3a surfaces it on the result); omitted otherwise → plain nav.
      const { scaffoldFirst } = result;
      // Web durability: also cache it per-game in localStorage so the chip
      // survives a refresh / re-nav on the crucible-web SPA (where the desktop's
      // crucible.json read via game:status isn't served over the cloud RPC).
      // Best-effort; an immutable create-time hint, so caching what we already
      // have is the right model. Alongside it, cache the original prompt so the
      // chip's "change" action can pre-fill New Game with it (same model — an
      // immutable create-time value the client already has).
      if (scaffoldFirst) {
        persistScaffoldFirst(result.gameId, scaffoldFirst);
        persistCreatePrompt(result.gameId, conceptText);
      }
      const routeState = buildCreateHandoffRouteState({
        prompt: conceptText,
        autoKickoff,
        scaffoldFirst,
        mode: wireInterviewDepth === 'advanced' ? 'design' : undefined,
      });
      setTimeout(
        () =>
          navigate(`/workspace/${result.gameId}`, routeState ? { state: routeState } : undefined),
        1200,
      );
    } catch (err) {
      setErrorState(unflattenIpcError(err));
      setStage('error');
    }
  }, [
    buildWithIngots,
    clearPromptDraft,
    concept,
    designDepthAvailable,
    interviewDepth,
    navigate,
    queryClient,
  ]);

  const armed = concept.trim().length > 0;
  // Hide the "Advanced" (Design-tab) depth when the feature flag is off or
  // the runtime lacks local filesystem access; legacy 'full' drafts are
  // normalised to Fast.
  const depthOptions = designDepthAvailable
    ? DEPTH_OPTIONS
    : DEPTH_OPTIONS.filter((d) => d.value !== 'advanced');
  const currentDepth =
    depthOptions.find((d) => d.value === interviewDepth) ??
    depthOptions.find((d) => d.value === 'quick')!;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <FoundryShellHeader
        chrome={frame === 'embedded' ? 'web' : 'electron'}
        showBrandMark={frame === 'fullscreen'}
        className={frame === 'embedded' ? 'border-b-0 bg-transparent' : undefined}
        leading={
          <Link
            to="/dashboard"
            className="app-region-no-drag flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.11] transition-all hover:border-white/[0.18]"
            style={{
              background: 'var(--color-crucible-bg)',
              color: 'var(--text-3)',
              transitionTimingFunction: 'var(--ease-flora)',
              transitionDuration: 'var(--motion-fast)',
            }}
            aria-label="Back to Dashboard"
          >
            <Home size={16} strokeWidth={1.75} />
          </Link>
        }
        actions={
          <div
            className="flex items-center gap-1 rounded-full px-1 py-1"
            style={{ background: 'var(--bg-elev-1)' }}
          >
            <button
              onClick={() => navigate('/settings')}
              className="rounded-full border border-white/[0.11] p-1.5 transition-all hover:border-white/[0.18]"
              style={{
                background: 'var(--color-crucible-bg)',
                color: 'var(--text-3)',
                transitionTimingFunction: 'var(--ease-flora)',
                transitionDuration: 'var(--motion-fast)',
              }}
              aria-label="Settings"
            >
              <SettingsIcon size={16} strokeWidth={1.75} />
            </button>
            {ghUser && (
              <img
                src={ghUser.avatarUrl}
                alt={ghUser.login}
                title={ghUser.login}
                className="h-8 w-8 rounded-full object-cover ring-1 ring-white/10"
              />
            )}
          </div>
        }
      />

      <main className="flex flex-1 flex-col items-center justify-center overflow-hidden px-6 pb-8">
        {stage === 'form' && (
          <div className="w-full max-w-3xl">
            {/* Gemini-style heading */}
            <div className="flex flex-col items-center pb-6">
              <h2
                className="text-center"
                style={{
                  color: 'var(--text-1)',
                  fontSize: '2.5rem',
                  fontWeight: 300,
                  letterSpacing: '-0.015em',
                }}
              >
                What would you like to build?
              </h2>
            </div>

            {/* Pill input */}
            <div
              className="flex items-end gap-2 rounded-[28px] px-3 py-3"
              style={{
                background: 'var(--surface-chat)',
                boxShadow: 'var(--surface-chat-shadow)',
              }}
            >
              {/* Plus / add context */}
              <button
                type="button"
                title="Add context (coming soon)"
                aria-label="Add context"
                className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                style={{ color: 'var(--text-3)' }}
              >
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path
                    d="M6.5 1v11M1 6.5h11"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>

              {/* Textarea */}
              <textarea
                ref={promptRef}
                value={concept}
                onChange={(e) => handleConceptChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && concept.trim())
                    void handleCreate();
                }}
                placeholder="Describe the game you want to build..."
                autoFocus
                rows={1}
                className="max-h-40 min-h-[26px] flex-1 resize-none overflow-y-auto border-0 bg-transparent text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
                style={{ color: 'var(--text-1)' }}
              />

              {concept.length > 0 && (
                <button
                  type="button"
                  title="Clear prompt"
                  aria-label="Clear prompt"
                  onClick={clearPromptDraft}
                  className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                  style={{ color: 'var(--text-3)' }}
                >
                  <X size={15} strokeWidth={1.9} />
                </button>
              )}

              {/* Fast / Advanced dropdown selector */}
              <div ref={depthRef} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setDepthOpen((o) => !o)}
                  aria-label="Kickoff mode"
                  aria-expanded={depthOpen}
                  className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] transition-colors hover:bg-white/[0.08]"
                  style={{ color: 'var(--text-2)' }}
                >
                  <span>{currentDepth.label}</span>
                  <ChevronDown
                    size={12}
                    strokeWidth={2}
                    className="transition-transform duration-150"
                    style={{ transform: depthOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  />
                </button>

                {depthOpen && (
                  <div
                    className="absolute bottom-full right-0 mb-3 w-64 overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
                    style={{ background: 'rgb(36, 36, 34)' }}
                  >
                    {depthOptions.map((opt, i) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleDepthSelect(opt.value)}
                        className={`flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.06] ${i > 0 ? 'border-t border-white/[0.06]' : ''}`}
                      >
                        <span className="mt-0.5 w-4 shrink-0 text-crucible-accent">
                          {interviewDepth === opt.value && <Check size={14} strokeWidth={2.5} />}
                        </span>
                        <span className="flex flex-col gap-0.5">
                          <span
                            className="text-[14px] font-medium"
                            style={{ color: 'var(--text-1)' }}
                          >
                            {opt.label}
                          </span>
                          <span className="text-[12px]" style={{ color: 'var(--text-3)' }}>
                            {opt.description}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mic (unarmed) → Arrow (armed) */}
              {armed ? (
                <button
                  type="button"
                  onClick={() => void handleCreate()}
                  data-testid="new-game-create-button"
                  aria-label="Create game"
                  className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-crucible-accent/20 text-crucible-accent-hover transition-colors hover:bg-crucible-accent/30 hover:text-white"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M8 13V3M3 8l5-5 5 5" />
                  </svg>
                </button>
              ) : (
                <button
                  type="button"
                  title="Voice input coming soon"
                  aria-label="Voice input (coming soon)"
                  className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                  style={{ color: 'var(--text-3)' }}
                >
                  <Mic size={16} strokeWidth={1.75} />
                </button>
              )}
            </div>

            <div className="mt-3 flex justify-center sm:justify-end">
              <button
                type="button"
                role="switch"
                aria-checked={buildWithIngots}
                aria-disabled={!ingotScaffolderAvailable}
                aria-label="Build with Ingots"
                title={
                  ingotScaffolderAvailable
                    ? 'Build with Ingots'
                    : 'Build with Ingots is unavailable in this runtime'
                }
                data-testid="new-game-ingots-toggle"
                disabled={!ingotScaffolderAvailable}
                onClick={handleBuildWithIngotsToggle}
                className={`inline-flex h-8 select-none items-center gap-2 rounded-full border px-3 text-[12px] transition-colors hover:bg-white/[0.06] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#171716] ${
                  buildWithIngots
                    ? 'border-crucible-accent/35 bg-crucible-accent/10'
                    : 'border-white/[0.08]'
                }`}
                style={{ color: buildWithIngots ? 'var(--text-1)' : 'var(--text-3)' }}
              >
                <span className="font-medium">Build with Ingots</span>
                <span
                  data-switch-track
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full p-0.5 transition-colors ${
                    buildWithIngots ? 'bg-crucible-accent/70' : 'bg-white/[0.14]'
                  }`}
                >
                  <span
                    data-switch-thumb
                    className={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      buildWithIngots ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </span>
              </button>
            </div>
          </div>
        )}

        {stage === 'creating' && (
          <div className="flex flex-col items-center gap-5">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/[0.12] border-t-white/50" />
            <p
              className="text-center text-[15px] transition-all duration-300"
              style={{ color: 'var(--text-2)' }}
            >
              {progressMessages.length > 0
                ? progressMessages[progressMessages.length - 1]
                : 'Creating your game...'}
            </p>
            {progressMessages.length > 1 && (
              <div className="flex flex-col items-center gap-1">
                {progressMessages.slice(0, -1).map((msg, i) => (
                  <p key={i} className="text-center text-[13px]" style={{ color: 'var(--text-3)' }}>
                    {msg}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}

        {stage === 'success' && (
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="#22c55e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold">Game Created!</h2>
            <p className="text-sm text-white/50">Opening workspace...</p>
          </div>
        )}

        {stage === 'error' && errorState && (
          <div className="flex w-full max-w-2xl flex-col gap-4">
            <h2 className="text-lg font-semibold">Creation Failed</h2>
            <CrucibleErrorState
              error={errorState}
              onRetry={() => {
                setStage('form');
                setProgressMessages([]);
                setErrorState(null);
                setRollbackWarning(null);
              }}
            />
            {rollbackWarning && (
              <RollbackWarningCard
                message={rollbackWarning.message}
                recovery={rollbackWarning.recovery}
              />
            )}
            {progressMessages.length > 0 && (
              <div className="max-h-32 overflow-auto rounded-lg bg-black/30 p-3">
                {progressMessages.map((msg, i) => (
                  <p key={i} className="text-xs text-white/50">
                    {msg}
                  </p>
                ))}
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/60 transition-colors hover:border-white/20 hover:text-white/80"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
